import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styles from "./HistoryItem.module.css";
import { DefaultButton } from "@fluentui/react";
import { Delete24Regular } from "@fluentui/react-icons";

export interface HistoryData {
    id: string;
    title: string;
    timestamp: number;
}

interface HistoryItemProps {
    item: HistoryData;
    onSelect: (id: string) => void;
    onDelete: (id: string) => void;
}

export function HistoryItem({ item, onSelect, onDelete }: HistoryItemProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const handleDelete = useCallback(() => {
        setIsModalOpen(false);
        onDelete(item.id);
    }, [item.id, onDelete]);
    const parseTitle = useCallback((title: string): string => {
        if (!title) return "VAE";

        try {
            let parsed: any = null;

            // Handle different JSON string formats
            if (title.startsWith('"{') && title.endsWith('}"')) {
                // Remove the outer quotes and parse: "{"subject":"","body":"..."}"
                const jsonString = title.slice(1, -1);
                parsed = JSON.parse(jsonString);
            } else if (title.startsWith("{") && title.endsWith("}")) {
                // Direct JSON object: {"subject":"","body":"..."}
                parsed = JSON.parse(title);
            } else if (title.startsWith('"{') || title.startsWith("{")) {
                // Handle truncated JSON - try to extract what we can using regex
                const subjectMatch = title.match(/"subject"\s*:\s*"([^"]*)"/);
                const bodyMatch = title.match(/"body"\s*:\s*"([^"]+)"/);

                // If subject exists and is not empty
                if (subjectMatch && subjectMatch[1].trim()) {
                    return subjectMatch[1].trim().length > 50 ? subjectMatch[1].trim().substring(0, 50) + "..." : subjectMatch[1].trim();
                }
                // If subject is empty but body exists, return "VAE"
                if (subjectMatch && !subjectMatch[1].trim() && bodyMatch) {
                    return "VAE";
                }
                // Fallback to body if no subject match
                if (bodyMatch) {
                    return bodyMatch[1].length > 50 ? bodyMatch[1].substring(0, 50) + "..." : bodyMatch[1];
                }
            } else {
                // Not a JSON string, return as-is (truncate if too long)
                return title.length > 50 ? title.substring(0, 50) + "..." : title;
            }
            if (parsed && typeof parsed === "object") {
                // **PRIORITY: Check for subject field**
                if ("subject" in parsed) {
                    let subject = typeof parsed.subject === "string" ? parsed.subject.trim() : "";

                    // If subject is empty, check if body contains double-encoded JSON with a subject
                    if (!subject && parsed.body && typeof parsed.body === "string") {
                        const body = parsed.body.trim();
                        if (body.startsWith("{") && body.includes('"subject"')) {
                            // Try to parse complete JSON first
                            try {
                                const innerParsed = JSON.parse(body);
                                if (innerParsed.subject && typeof innerParsed.subject === "string") {
                                    subject = innerParsed.subject.trim();
                                }
                            } catch (e) {
                                // If parse fails (likely truncated), extract using regex
                                const innerSubjectMatch = body.match(/"subject"\s*:\s*"([^"\\]*(\\.[^"\\]*)*)"/);

                                if (innerSubjectMatch && innerSubjectMatch[1]) {
                                    const extractedSubject = innerSubjectMatch[1].trim();
                                    // Safely unescape JSON-style escape sequences using JSON.parse
                                    try {
                                        subject = JSON.parse(`"${extractedSubject}"`);
                                    } catch {
                                        // Fallback to the raw extracted subject if parsing fails
                                        subject = extractedSubject;
                                    }
                                    // console.log('[HistoryItem] Extracted inner subject via regex:', subject);
                                } else {
                                    console.debug("Could not extract inner subject from double-encoded body");
                                }
                            }
                        }
                    }

                    // If subject is still empty after checking double-encoding, return "VAE"
                    if (!subject) {
                        return "VAE";
                    }
                    // If subject exists and not empty, return it (50 char limit)
                    return subject.length > 50 ? subject.substring(0, 50) + "..." : subject;
                }

                // Fallback to other fields if no subject field exists
                const candidates = [parsed.title, parsed.body, parsed.message, parsed.content, parsed.text];

                for (const candidate of candidates) {
                    if (candidate && typeof candidate === "string" && candidate.trim()) {
                        const cleanTitle = candidate.trim();
                        return cleanTitle.length > 50 ? cleanTitle.substring(0, 50) + "..." : cleanTitle;
                    }
                }

                // If no meaningful text found, return "VAE"
                return "VAE";
            }
        } catch (error) {
            // If parsing fails, check if it's a simple truncated message
            console.debug("Failed to parse title as JSON, using original:", error);
        }

        // Return the original title if it's not JSON or parsing failed
        return title.length > 50 ? title.substring(0, 50) + "..." : title;
    }, []);
    const displayTitle = parseTitle(item.title);
    // Extract body text for subtitle
    const getAdditionalInfo = useCallback((title: string): string | null => {
        // console.log('[HistoryItem] Parsing title for subtitle:', title);
        try {
            let parsed: any = null;
            let titleToProcess = title;

            // Handle truncated JSON - detect and repair
            if ((title.startsWith("{") || title.startsWith('"{')) && !title.endsWith("}") && !title.endsWith('}"')) {
                // console.log('[HistoryItem] Detected truncated JSON, attempting to repair');

                // Try to extract body using regex even from incomplete JSON
                const bodyMatch = title.match(/"body"\s*:\s*"([^"\\]*(\\.[^"\\]*)*)"/);
                if (bodyMatch && bodyMatch[1]) {
                    const body = bodyMatch[1].trim();
                    // Unescape common escape sequences
                    const unescapedBody = body.replace(/\\n/g, "\n").replace(/\\"/g, '"').replace(/\\\\/g, "\\");
                    // console.log('[HistoryItem] Extracted body from truncated JSON:', unescapedBody);
                    return unescapedBody.length > 60 ? unescapedBody.substring(0, 60) + "..." : unescapedBody;
                }

                // If we can't extract body, try to repair the JSON by adding closing braces
                if (title.startsWith('"{') && !title.endsWith('}"')) {
                    titleToProcess = title + '"}';
                } else if (title.startsWith("{") && !title.endsWith("}")) {
                    titleToProcess = title + "}";
                }
                // console.log('[HistoryItem] Repaired JSON:', titleToProcess);
            }

            // Try to parse the JSON (original or repaired)
            if (titleToProcess.startsWith('"{') && titleToProcess.endsWith('}"')) {
                const jsonString = titleToProcess.slice(1, -1);
                parsed = JSON.parse(jsonString);
            } else if (titleToProcess.startsWith("{") && titleToProcess.endsWith("}")) {
                parsed = JSON.parse(titleToProcess);
            }
            if (parsed && typeof parsed === "object") {
                // **PRIORITY: Show body as subtitle**
                if (parsed.body && typeof parsed.body === "string" && parsed.body.trim()) {
                    let cleanBody = parsed.body.trim();

                    // Check if body is double-encoded JSON string (even if truncated)
                    if (cleanBody.startsWith("{") && cleanBody.includes('"body"')) {
                        // Try to parse complete JSON first
                        try {
                            const innerParsed = JSON.parse(cleanBody);
                            if (innerParsed.body && typeof innerParsed.body === "string") {
                                cleanBody = innerParsed.body.trim();
                            }
                        } catch (e) {
                            // If parse fails (likely truncated), extract using regex

                            // Look for escaped \"body\":\" pattern in the double-encoded string
                            const innerBodyMatch = cleanBody.match(/\\\"body\\\"\\s*:\\s*\\\"([^\"\\]*(\\.[^\"\\]*)*)/);

                            if (innerBodyMatch && innerBodyMatch[1]) {
                                const extractedBody = innerBodyMatch[1].trim();
                                // Use JSON.parse to unescape the extracted value in one safe pass.
                                // If that fails (e.g. truncated/malformed fragment), fall back to
                                // the raw extracted text rather than chained manual replacements,
                                // which risk double-unescaping (CodeQL js/double-escaping).
                                try {
                                    cleanBody = JSON.parse('"' + extractedBody + '"');
                                } catch {
                                    cleanBody = extractedBody;
                                }
                            }
                        }
                    }

                    const result = cleanBody.length > 60 ? cleanBody.substring(0, 60) + "..." : cleanBody;
                    return result;
                }
                // Fallback to content or message if body doesn't exist
                if (parsed.content && typeof parsed.content === "string" && parsed.content.trim()) {
                    const cleanContent = parsed.content.trim();
                    return cleanContent.length > 60 ? cleanContent.substring(0, 60) + "..." : cleanContent;
                }

                if (parsed.message && typeof parsed.message === "string" && parsed.message.trim()) {
                    const cleanMessage = parsed.message.trim();
                    return cleanMessage.length > 60 ? cleanMessage.substring(0, 60) + "..." : cleanMessage;
                }
            }
        } catch (error) {
            // Ignore parsing errors
            console.debug("Failed to parse additional info:", error);
        }
        return null;
    }, []);

    const additionalInfo = getAdditionalInfo(item.title);

    return (
        <div className={styles.historyItem}>
            <button
                onClick={() => onSelect(item.id)}
                className={styles.historyItemButton}
                title={additionalInfo || displayTitle} // Show full info on hover
            >
                <div className={styles.historyItemContent}>
                    <div className={styles.historyItemTitle}>{displayTitle}</div>
                    {additionalInfo && <div className={styles.historyItemSubtitle}>{additionalInfo}</div>}
                </div>
            </button>
            <button onClick={() => setIsModalOpen(true)} className={styles.deleteButton} aria-label="delete this chat history">
                <Delete24Regular className={styles.deleteIcon} />
            </button>
            <DeleteHistoryModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onConfirm={handleDelete} />
        </div>
    );
}

function DeleteHistoryModal({ isOpen, onClose, onConfirm }: { isOpen: boolean; onClose: () => void; onConfirm: () => void }) {
    if (!isOpen) return null;
    const { t } = useTranslation();
    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
                <h2 className={styles.modalTitle}>{t("history.deleteModalTitle")}</h2>
                <p className={styles.modalDescription}>{t("history.deleteModalDescription")}</p>
                <div className={styles.modalActions}>
                    <DefaultButton onClick={onClose} className={styles.modalCancelButton}>
                        {t("history.cancelLabel")}
                    </DefaultButton>
                    <DefaultButton onClick={onConfirm} className={styles.modalConfirmButton}>
                        {t("history.deleteLabel")}
                    </DefaultButton>
                </div>
            </div>
        </div>
    );
}
